/**
 * StudyGroupDo adapter for the unified sync engine.
 *
 * Mirrors `user-do-adapter.ts` but for the per-group scope. The engine
 * handles the universal plumbing (decode, log, op-ack / op-rejected,
 * persistence-error). The adapter knows about the SG protocol shape
 * and which slice of `useStudyGroupStore` it writes to.
 *
 * The per-entity merge logic (mergeMembers, mergeFolders, mergeCards,
 * mergeDeckProps, etc.) is still hand-mapped against the existing SG
 * store setters — a future refactor can regenerate them from the entity
 * registry once the store-setter rename lands.
 */

import { getSyncClientCapabilities } from '@flashka/shared/sync-engine/framework/client-capabilities';
import {
  type LWWItem,
  sameEntryContent,
} from '@flashka/shared/sync-engine/framework/define-sync-entity';
import type {
  SGActivityEntry,
  SGDeckMindmapEntry,
  SGDeckProps,
  SGDrawSettings,
  SGFolder,
  SGMember,
  StudyGroupMeta,
} from '@flashka/shared/sync-engine/schema/group-entities';
import { sgScoreDeltaAckSchema } from '@flashka/shared/sync-engine/schema/sg-score-wire';
import type { SyncCardProps } from '@flashka/shared/sync-engine/schema/shared-types';

import { resendPendingDeckRemovals } from '../../study-groups/deck-removal';
import {
  clientGroupBroadcastMap,
  clientGroupEntities,
  type ClientSyncEntity,
} from '../entities/client-entities';
import { perfLog, perfNow } from '../perf-log'; // [perf-probe]
import { useFlashkaStore } from '../store/flashka-store';
import { type SgRowViewer, useStudyGroupStore } from '../store/study-group-store';
import {
  flushVersionKey,
  isFlushVersionRejected,
  peekFlushMutationVersion,
  recordFlushMutation,
} from '../sync-bookkeeping/flush-reject-guard';
import { generateMutationId } from '../sync-bookkeeping/pending-mutations';
import type { ScopeAdapter } from './use-do-sync';

// ---------------------------------------------------------------------------
// Wire message shapes — local types; the server emits these on the SG WS.
// ---------------------------------------------------------------------------

export type SGSyncInitMsg = {
  type: 'sg-sync-init';
  /** Version-only fast-path init carries ONLY this (no deckTimestamps). The
   *  server replies `sg-sync-in-sync` if it matches the group's commit version,
   *  else `sg-sync-request-manifest`. Omitted on the full-manifest init. */
  lastVersion?: number;
  /** Full-manifest fields — omitted on the version-only fast-path init. */
  deckTimestamps?: Record<string, string>;
  /**
   * `lazySgCards` capability (full manifest only): stream cards only for the
   * decks listed in `deckTimestamps`; a stale deck absent from it rides the
   * header as properties alone and is fetched on demand with
   * `sg-request-deck-cards`. Absent ⇒ every stale deck streams.
   */
  lazyCards?: true;
  /**
   * This client understands the sync-init STREAM (header → card frames →
   * `sg-decks-loaded`). The server replies with the pre-stream ONE-message
   * diff to anything that does not advertise it — every build shipped before
   * the stream, which reads the commit version off `sg-sync-diff` only and
   * would otherwise record a version from a mid-stream message and build its
   * next manifest from a partial bucket.
   */
  cardsStreamed?: true;
  __sgMeta?: string;
  __sgMembers?: string;
  __sgFolders?: string;
  recordTimestamps?: Record<string, Record<string, string>>;
};

export type SGSyncDiffMsg = {
  type: 'sg-sync-diff';
  /** The group's commit version as of this diff — stamped here only by a
   *  server that ships the whole diff in one message. A streaming server
   *  stamps it on `sg-decks-loaded` instead (see `cardsStreamed`). The client
   *  persists it so the next reconnect can take the version-only fast path. */
  version?: number;
  /**
   * The diff is the HEADER of a stream: `decks[*].cards` is empty and the
   * bodies follow as `sg-cards-bulk-updated` frames, closed by an
   * `sg-decks-loaded` frame that carries the version. Until that frame lands
   * the client records no version, pushes no offline edit, and holds every
   * listed live deck as NOT loaded (a stale deck it held complete is partial
   * while its frames land) — a disconnect mid-stream must leave the next
   * connect on the full manifest, with no timestamp for those decks, never on
   * a version or a timestamp that stands for cards this client never received.
   */
  cardsStreamed?: boolean;
  serverDeckIds: string[];
  decks?: Record<
    string,
    { cards?: SyncCardProps[]; properties: SGDeckProps; updatedAtISO: string }
  >;
  generics?: {
    sgMeta?: { sgMeta: StudyGroupMeta; updatedAtISO: string };
    sgMembers?: { sgMembers: SGMember[]; updatedAtISO: string };
    sgFolders?: { sgFolders: SGFolder[]; updatedAtISO: string };
  };
  deckProperties?: Record<string, SGDeckProps>;
  serverDeckPropertiesKeys?: string[];
  sgActivity?: Record<string, SGActivityEntry>;
  sgDeckMindmaps?: Record<string, SGDeckMindmapEntry>;
  serverSgActivityKeys?: string[];
};

/**
 * "These decks' card sets are complete on this client now." Sent by the
 * server after a sync-init stream's frames (with the group's commit
 * `version` — what tells the client the whole reply landed), after a peer's
 * compound deck push, and after an on-demand deck load (both without a
 * version). Always sent, even when no card frame preceded it, so "complete
 * with zero cards" is distinguishable from "still streaming".
 */
export type SGDecksLoadedMsg = {
  type: 'sg-decks-loaded';
  deckIds: string[];
  version?: number;
};

/**
 * Server→owner bundle of parked legacy notes for the decks this user
 * owns. `deckNotes` is keyed by `sgDeckId` → `noteId` → the raw legacy
 * `{ content, updatedAtISO }` entry. Direct analogue of the personal
 * `legacy-notes-bundle` (see user-do-adapter.ts), owner-filtered
 * server-side.
 */
export type SGLegacyNotesBundleMsg = {
  type: 'sg-legacy-notes-bundle';
  deckNotes: Record<string, Record<string, unknown>>;
};

/**
 * Platform-supplied stash bridge for the SG `legacy-notes-bundle`.
 * Web wires a Dexie-backed stash; native an AsyncStorage one;
 * undefined → no-op (the ack still fires, mirroring personal). Keyed
 * per (userId, groupId) so a device with multiple accounts/groups
 * doesn't collide.
 */
export type SGLegacyMigrationClient = {
  stashSGLegacyNotes: (
    userId: string,
    groupId: string,
    deckNotes: Record<string, Record<string, unknown>>,
  ) => Promise<void> | void;
};

// ---------------------------------------------------------------------------
// Adapter factory
// ---------------------------------------------------------------------------

export type StudyGroupDoAdapterDeps = {
  groupId: string;
  /** Signed-in user — needed to scope the legacy-notes stash. */
  userId?: string | null;
  /** Platform legacy-notes stash (undefined → bundle is ack'd + dropped). */
  sgLegacyClient?: SGLegacyMigrationClient;
};

/** Bookkeeping for a sync-init reply that is still streaming its frames. */
type PendingStream = {
  /** [perf-probe] when the header landed. */
  t0: number;
  frames: number;
  cards: number;
  /** Highest `sg-sync-version` broadcast seen mid-stream — recorded at the
   *  completion frame, never before (see `SGSyncDiffMsg.cardsStreamed`). */
  version: number | null;
};

export function createStudyGroupDoAdapter(deps: StudyGroupDoAdapterDeps): ScopeAdapter {
  const { groupId, userId, sgLegacyClient } = deps;
  // The sync-init reply in flight, from a `cardsStreamed` header until its
  // `sg-decks-loaded`. Null between replies and after a disconnect (a stream
  // cut by the socket is simply abandoned — the reconnect re-inits).
  let pendingStream: PendingStream | null = null;

  return {
    // NOTE: no `onConnected` send. The server ALWAYS emits `sg-sync-state`
    // after the Yjs handshake (SyncYServer.onAfterSyncHandshake), and the
    // handler below sends `sg-sync-init` in response — exactly once per
    // connect. The previous `onConnected` send fired a SECOND init on every
    // connect (the engine fires onConnected on warm-open AND on `sync`), so
    // the server built + shipped the full sg-sync-diff twice. This mirrors
    // UserDo, which has no onConnected and inits only on `sync-state: ready`.
    onProtocolMessage: async (raw, send) => {
      const msg = raw as { type: string } & Record<string, unknown>;
      const store = useStudyGroupStore.getState();

      switch (msg.type) {
        case 'sg-sync-state':
          if ((msg as unknown as { status: string }).status === 'ready') {
            store.setConnectionStatus(groupId, 'connected');
          }
          // A new handshake supersedes whatever reply was in flight.
          pendingStream = null;
          // Always (re)send sync-init when the server announces ready —
          // including the needs-seed branch, where the server still
          // wants our timestamps before deciding what to ship.
          send(JSON.stringify(buildSgSyncInit(groupId)));
          return true;

        case 'sg-sync-error':
          console.warn(`[sg-sync ${groupId}] server error:`, msg.message);
          return true;

        case 'sg-sync-diff': {
          const perfT0 = perfNow(); // [perf-probe]
          const diff = msg as unknown as SGSyncDiffMsg;
          applySgSyncDiff(groupId, diff);
          perfLog('sg:diff-apply', perfT0, () => {
            const decks = diff.decks ?? {};
            let cards = 0;
            for (const d of Object.values(decks)) cards += d.cards?.length ?? 0;
            return {
              groupId: groupId.slice(0, 8),
              decks: Object.keys(decks).length,
              cards,
              streamed: diff.cardsStreamed === true,
            };
          });
          if (diff.cardsStreamed) {
            // Header of a stream: the cards, the version and the flush all
            // wait for `sg-decks-loaded`. Every live deck it lists is about to
            // be re-shipped, so a deck this client held COMPLETE is incomplete
            // from here until the completion frame — unload it now. If the
            // socket drops mid-stream the flag stays off: the deck's row is
            // dropped, its timestamp leaves the manifest, and the next connect
            // ships it whole instead of skipping it on a bucket that holds the
            // deck's newest card but not all of them.
            store.markDecksUnloaded(groupId, liveDeckIds(diff));
            pendingStream = { t0: perfT0, frames: 0, cards: 0, version: null };
            return true;
          }
          // One-message diff (a server without the stream): every shipped
          // deck is complete now.
          store.markDecksLoaded(groupId, liveDeckIds(diff));
          flushSgLocalDirty(groupId, send);
          recordSgVersion(groupId, diff.version);
          return true;
        }

        // End of a sync-init stream (carries `version`), of a peer's compound
        // deck push or of an on-demand load (no version): the listed decks'
        // card sets are complete. Only the stream's completion flushes offline
        // edits and records the version — an on-demand load lands while the
        // group is already in sync, and a flush there would re-push edits
        // whose acks are still in flight.
        case 'sg-decks-loaded': {
          const loaded = msg as unknown as SGDecksLoadedMsg;
          // Only decks this client knows: an on-demand load of a deck the group
          // no longer has completes with zero cards, and marking THAT loaded
          // would mint an empty bucket (and a row) for a deck without props.
          const known = store.groups[groupId]?.deckProps ?? {};
          const deckIds = (Array.isArray(loaded.deckIds) ? loaded.deckIds : []).filter(
            (id): id is string => typeof id === 'string' && id in known,
          );
          store.markDecksLoaded(groupId, deckIds);
          const stream = typeof loaded.version === 'number' ? pendingStream : null;
          if (stream) {
            pendingStream = null;
            perfLog('sg:cards-stream', stream.t0, () => ({
              groupId: groupId.slice(0, 8),
              decks: deckIds.length,
              frames: stream.frames,
              cards: stream.cards,
            }));
            flushSgLocalDirty(groupId, send);
            recordSgVersion(groupId, loaded.version);
            recordSgVersion(groupId, stream.version ?? undefined);
          }
          return true;
        }

        // Version-cursor fast path: the group's commit version matches ours, so
        // nothing changed server-side. Flush any edits made while disconnected,
        // persist the version, mark connected — no diff, no whole-group re-ship.
        case 'sg-sync-in-sync':
          store.setConnectionStatus(groupId, 'connected');
          recordSgVersion(groupId, (msg as { version?: number }).version);
          flushSgLocalDirty(groupId, send);
          return true;

        // Versions diverged — send the full manifest so the server can compute
        // a precise diff.
        case 'sg-sync-request-manifest':
          send(JSON.stringify(buildSgSyncInit(groupId, { forceFull: true })));
          return true;

        // A committed write advanced the group's commit version (ours or
        // another member's). Persist it (monotonic) so our next reconnect can
        // fast-path instead of re-syncing the whole group. Mid-stream it is
        // held back until the completion frame: recording it now and losing
        // the socket before the frames finish would make the next connect
        // skip cards this client never got.
        case 'sg-sync-version': {
          const version = (msg as { version?: number }).version;
          if (pendingStream) {
            if (typeof version === 'number') {
              pendingStream.version = Math.max(pendingStream.version ?? 0, version);
            }
            return true;
          }
          recordSgVersion(groupId, version);
          return true;
        }

        case 'sg-deck-deleted':
          // Legacy compound wire — kept for back-compat with the server's
          // double-broadcast on deck removal.
          store.removeDeck(groupId, msg.deckId as string);
          return true;

        // ── Leaderboard ack (single-sender, NOT broadcast) ──────────────
        // The server sends `sg-score-ack` directly to the pushing member
        // after `applyScoreDelta`. We route it into the store's
        // `liveScore` slice so `sgGroup(id).leaderboard.liveScore()`
        // returns server-authoritative scalars — and so the NEXT card's
        // multiplier choice matches the server's post-bump streak.
        //
        // The framework's auto-derived broadcast map for `sgScores` uses
        // a sentinel type (`sg-score-broadcast-noop`) precisely so this
        // wire doesn't get routed through the LWW merger (the ack
        // payload isn't a full row).
        case 'sg-score-ack': {
          const parsed = sgScoreDeltaAckSchema.safeParse(msg);
          if (!parsed.success) {
            if (process.env.NODE_ENV === 'development') {
              console.warn(
                `[sg-score-ack ${groupId}] invalid payload`,
                parsed.error.issues,
              );
            }
            return true; // claim handled — drop silently
          }
          const { streak, totalMP, todayMP } = parsed.data;
          store.setLiveScore(groupId, {
            streak,
            totalMP,
            todayMP,
            lastUpdatedAtISO: new Date().toISOString(),
          });
          return true;
        }

        // ── Legacy notes bundle (owner-only; server-filtered) ───────────
        // Mirror of the personal `legacy-notes-bundle` handler. Stash the
        // parked notes so the owner's editor seeds the shared Y.Doc on
        // first mount. ALWAYS ack (with the sgDeckIds we received) so the
        // DO can drop those shards per-deck — even if stashing failed, we
        // prefer losing legacy notes to the DO holding them forever (30d
        // TTL is the backstop, not the primary path).
        case 'sg-legacy-notes-bundle': {
          const deckNotes = (msg as SGLegacyNotesBundleMsg).deckNotes ?? {};
          try {
            if (userId && sgLegacyClient && Object.keys(deckNotes).length > 0) {
              await sgLegacyClient.stashSGLegacyNotes(userId, groupId, deckNotes);
            }
          } catch (err) {
            console.warn(`[sg-sync ${groupId}] stashSGLegacyNotes failed:`, err);
          }
          send(
            JSON.stringify({
              type: 'sg-legacy-notes-stashed-ack',
              sgDeckIds: Object.keys(deckNotes),
            }),
          );
          return true;
        }

        default: {
          // Registry-driven entity dispatch.
          const entity = clientGroupBroadcastMap[msg.type];
          if (!entity) return false;
          await applySgEntityBroadcast(groupId, entity, msg);
          if (
            pendingStream &&
            entity.name === 'cards' &&
            sgWireKey(entity, 'bulkUpdate') === msg.type
          ) {
            pendingStream.frames += 1;
            pendingStream.cards += (msg as { entries?: unknown[] }).entries?.length ?? 0;
          }
          return true;
        }
      }
    },

    subscribeDirty: () => {
      // The SG path doesn't auto-push on every store change — it pushes
      // explicitly via `flushSgLocalDirty` after a sync-diff arrives.
      // This means cleanup is a no-op.
      //
      // Adapter-driven sends are the right shape for the SG path because
      // the SG store mutations from local UI go through the public
      // `sync.studyGroups` facade, which constructs the outbound wire
      // directly. The flushSgLocalDirty path catches anything that
      // diverged while the WS was disconnected.
      return () => {};
    },

    onDisconnected: () => {
      pendingStream = null;
      useStudyGroupStore.getState().setConnectionStatus(groupId, 'disconnected');
    },

    onPushAck: (mutationId) => {
      advanceSgCardClockFromAck(mutationId);
      advanceSgDeckMetaCursorFromAck(mutationId);
    },
  };
}

// ---------------------------------------------------------------------------
// Sync-init payload
// ---------------------------------------------------------------------------

/** serverTimestamps key under which the per-group commit version is persisted
 *  (stored as a string in the same persisted map as the entity clocks). */
const SG_VERSION_TS_KEY = '__version';

function buildSgSyncInit(groupId: string, opts?: { forceFull?: boolean }): SGSyncInitMsg {
  const slice = useStudyGroupStore.getState().groups[groupId];
  const ts = slice?.serverTimestamps ?? {};

  // Version-only fast path: send JUST the cursor when we have a persisted
  // version AND local group data. They live in the same persisted slice, so a
  // cleared/evicted store has neither → we fall through to the full manifest
  // (a safe full resync). `forceFull` is the reply to sg-sync-request-manifest.
  const rawVersion = ts[SG_VERSION_TS_KEY];
  const lastVersion = rawVersion != null && rawVersion !== '' ? Number(rawVersion) : null;
  const hasLocalData =
    !!slice &&
    (Object.keys(slice.deckProps).length > 0 ||
      Object.keys(slice.cards).length > 0 ||
      !!slice.meta);
  if (
    !opts?.forceFull &&
    lastVersion != null &&
    !Number.isNaN(lastVersion) &&
    hasLocalData
  ) {
    return { type: 'sg-sync-init', lastVersion };
  }

  // Full manifest (cold start / cleared store / phase-2 reply).
  const deckTimestamps: Record<string, string> = {};
  // Per-deck LWW guard — send the highest SHARED card timestamp we've
  // seen for each deck so the server can skip sending us cards we already
  // have. Shared scope only: a personal-only edit bumps `updatedAtISO`
  // but not `sharedUpdatedAtISO`; using the aggregate would inflate the
  // deck timestamp and make the server withhold a peer's genuinely-newer
  // shared card (the inbound twin of the outbound churn). LOADED decks only:
  // a bucket left partial by a stream the socket cut can hold the deck's
  // newest card, and a timestamp built from it would make the server skip
  // the deck for good. No timestamp ⇒ the server ships the deck whole (or,
  // under `lazySgCards`, its properties alone — see `lazyCards`). Every loaded
  // deck gets an entry, an empty one when it holds no shared card: under
  // `lazyCards` the entry is what asks the server to stream the deck, and a
  // deck this client holds complete must keep receiving its cards.
  if (slice) {
    for (const deckId of slice.loadedDeckIds) {
      let maxTs = '';
      for (const c of Object.values(slice.cards[deckId] ?? {})) {
        const shared = c.sharedUpdatedAtISO ?? c.updatedAtISO ?? '';
        if (shared > maxTs) maxTs = shared;
      }
      deckTimestamps[deckId] = maxTs;
    }
    // A deck that is NOT loaded but HOLDS rows is a deck whose stream was cut
    // (the header unloads every deck it is about to re-ship). Leaving it out
    // of the manifest is what `lazyCards` reads as "never loaded — properties
    // alone", so its bucket stayed the cut-off subset for the rest of the
    // session and every reconnect: a member whose mirror was already minted
    // has no other re-requester (`useHydrateSgDeckCards` runs only for decks
    // WITHOUT a mirror), so the shared cards never came back. An EMPTY entry
    // asks for the deck whole — never a timestamp from the partial bucket,
    // which would make the server skip it for good.
    for (const deckId of Object.keys(slice.cards)) {
      if (slice.loadedDeckIds.has(deckId)) continue;
      if (Object.keys(slice.cards[deckId] ?? {}).length === 0) continue;
      deckTimestamps[deckId] = '';
    }
  }
  return {
    type: 'sg-sync-init',
    deckTimestamps,
    // Tell the server this client understands the STREAM (header → card frames
    // → `sg-decks-loaded`). Without it the server replies with the pre-stream
    // one-message diff, which is what every build that predates the stream
    // still needs — see `SgSyncInitManifest.cardsStreamed`.
    cardsStreamed: true as const,
    ...(getSyncClientCapabilities().lazySgCards ? { lazyCards: true as const } : {}),
    __sgMeta: ts.__sgMeta,
    __sgMembers: ts.__sgMembers,
    __sgFolders: ts.__sgFolders,
  };
}

/**
 * Persist the group's commit version (monotonic) into the SG store's
 * `serverTimestamps['__version']`, so the next sg-sync-init can fast-path.
 * Lives in the same persisted slice as the entity clocks, so it survives
 * reloads and is cleared together with the group data (the cleared-store
 * guard in buildSgSyncInit relies on that).
 */
function recordSgVersion(groupId: string, version: unknown): void {
  if (typeof version !== 'number') return;
  const store = useStudyGroupStore.getState();
  const raw = store.groups[groupId]?.serverTimestamps?.[SG_VERSION_TS_KEY];
  const current = raw != null && raw !== '' ? Number(raw) : 0;
  if (Number.isNaN(current) || version > current) {
    store.setServerTs(groupId, SG_VERSION_TS_KEY, String(version));
  }
}

// ---------------------------------------------------------------------------
// Entity broadcast dispatch — mirror-aware key resolution
// ---------------------------------------------------------------------------

function sgWireKey(
  e: ClientSyncEntity,
  variant: 'update' | 'bulkUpdate' | 'delete' | 'bulkDelete',
): string | undefined {
  if (variant === 'update') {
    return e.mirror === 'split' && e.groupBroadcastType
      ? e.groupBroadcastType
      : e.broadcastType;
  }
  if (variant === 'bulkUpdate') {
    return e.mirror === 'split' && e.groupBulkBroadcastType
      ? e.groupBulkBroadcastType
      : e.bulkBroadcastType;
  }
  if (variant === 'delete') {
    return e.mirror === 'split' && e.groupDeleteBroadcastType
      ? e.groupDeleteBroadcastType
      : e.deleteBroadcastType;
  }
  return e.mirror === 'split' && e.groupBulkDeleteBroadcastType
    ? e.groupBulkDeleteBroadcastType
    : e.bulkDeleteBroadcastType;
}

function sgTsKey(e: ClientSyncEntity): string {
  return e.mirror === 'split' && e.groupTsKey ? e.groupTsKey : e.tsKey;
}

async function applySgEntityBroadcast(
  groupId: string,
  entity: ClientSyncEntity,
  msg: { type: string } & Record<string, unknown>,
): Promise<void> {
  const wireType = msg.type;
  if (sgWireKey(entity, 'bulkUpdate') === wireType) {
    applySgBulkUpdate(groupId, entity, msg as unknown as SgBulkUpdateBroadcast);
    return;
  }
  if (sgWireKey(entity, 'bulkDelete') === wireType) {
    applySgBulkDelete(groupId, entity, msg as unknown as SgBulkDeleteBroadcast);
    return;
  }
  if (sgWireKey(entity, 'delete') === wireType) {
    applySgDelete(groupId, entity, msg as unknown as SgDeleteBroadcast);
    return;
  }
  applySgUpdate(groupId, entity, msg);
}

// ---------------------------------------------------------------------------
// Per-variant dispatch — bridges entity-shape to hand-written setters
// ---------------------------------------------------------------------------

type SgDeleteBroadcast = { type: string; key: string; deletedTimeISO: string };
type SgBulkUpdateBroadcast = {
  type: string;
  entries: Array<{ key: string; entry: LWWItem; updatedAtISO?: string }>;
  updatedAtISO?: string;
};
type SgBulkDeleteBroadcast = {
  type: string;
  keys: Array<{ key: string; deletedTimeISO?: string }>;
  updatedAtISO?: string;
};

function applySgUpdate(
  groupId: string,
  entity: ClientSyncEntity,
  msg: Record<string, unknown>,
): void {
  const store = useStudyGroupStore.getState();
  const updatedAtISO = msg.updatedAtISO as string | undefined;

  if (entity.type === 'value') {
    const data = entity.extractFromMessage(msg);
    if (entity.name === 'sgMeta') {
      store.setMeta(groupId, data as StudyGroupMeta);
    }
    if (entity.name === 'sgDrawSettings') {
      store.setDrawSettings(groupId, data as SGDrawSettings);
    }
    if (updatedAtISO) store.setServerTs(groupId, sgTsKey(entity), updatedAtISO);
    return;
  }

  if (entity.type === 'collection' && entity.primaryKey) {
    const items = entity.extractFromMessage(msg) as LWWItem[];
    if (entity.name === 'sgMembers') {
      store.mergeMembers(groupId, items as unknown as SGMember[]);
    } else if (entity.name === 'sgFolders') {
      store.mergeFolders(groupId, items as unknown as SGFolder[]);
    }
    if (updatedAtISO) store.setServerTs(groupId, sgTsKey(entity), updatedAtISO);
    return;
  }

  if (entity.type === 'record') {
    const key = msg.key as string;
    const entry = msg.entry as LWWItem;
    if (entity.nested) {
      // Nested record (cards): compound key `deckId\x01cardId`.
      const sep = key.indexOf('\x01');
      if (sep === -1) return;
      const deckId = key.slice(0, sep);
      const incomingCard = entry as unknown as SyncCardProps;
      store.mergeCards(groupId, deckId, [incomingCard]);
      recordReceivedSgCardClock(groupId, deckId, [incomingCard]);
      return;
    }
    if (entity.name === 'deckProperties') {
      const kept = mergeSgDeckPropsKeepingLocal(groupId, [
        entry as unknown as SGDeckProps,
      ]);
      // Same discipline as the sync-diff below: a cursor stamped past a row
      // the merge kept local would strand that half on this device.
      if (updatedAtISO && !kept)
        store.setServerTs(groupId, sgTsKey(entity), updatedAtISO);
    } else if (entity.name === 'sgActivity') {
      store.upsertActivity(groupId, entry as unknown as SGActivityEntry);
    } else if (entity.name === 'sgDeckMindmaps') {
      store.upsertDeckMindmap(groupId, {
        ...(entry as object),
        key,
      } as SGDeckMindmapEntry);
    }
  }
}

/**
 * `mergeDeckProps` for rows the SERVER sent, reporting whether the merge
 * kept anything a shipped row lacked — an offline note in the materials
 * index, or a local edit newer than the row. The group's deck-meta cursor
 * (`__sgdeckmeta`, one clock for every row of the group) is then left where
 * it was: `flushSgLocalDirty` pushes every row past it, the group DO folds
 * the kept half in (and writes nothing for a row it already holds), and the
 * push's op-ack advances the cursor (`advanceSgDeckMetaCursorFromAck`).
 * Stamping the cursor at the shipped rows' clock instead — what happened
 * before — marked the merged row as synced with the note still only here:
 * a linked deck's note made on the phone never reached the group.
 *
 * The UserDo path keeps the same rule per row (`applySyncDiff`); the group
 * cursor is per group, so one kept row holds the whole cursor back until
 * its ack. The extra pushes that costs (rows the diff shipped clean) are
 * no-ops on the server.
 */
function mergeSgDeckPropsKeepingLocal(groupId: string, rows: SGDeckProps[]): boolean {
  const before = useStudyGroupStore.getState().groups[groupId]?.deckProps ?? {};
  useStudyGroupStore.getState().mergeDeckProps(groupId, rows, serverRowViewer());
  const after = useStudyGroupStore.getState().groups[groupId]?.deckProps ?? {};
  let kept = false;
  for (const row of rows) {
    if (row.deletedTimeISO) continue;
    const prev = before[row.id];
    const merged = after[row.id];
    if (prev && merged && !sameEntryContent(merged, row)) kept = true;
  }
  return kept;
}

/** The signed-in user every server-sent deck-props row was addressed to —
 *  a hidden-source row reaches everyone else without its documents. */
function serverRowViewer(): SgRowViewer {
  return { userId: useFlashkaStore.getState().currentUserId };
}

function applySgDelete(
  groupId: string,
  entity: ClientSyncEntity,
  msg: SgDeleteBroadcast,
): void {
  const store = useStudyGroupStore.getState();

  if (entity.type === 'record' && entity.nested) {
    const sep = msg.key.indexOf('\x01');
    if (sep === -1) return;
    const deckId = msg.key.slice(0, sep);
    const cardId = msg.key.slice(sep + 1);
    const existing = store.groups[groupId]?.cards[deckId]?.[cardId];
    if (existing) {
      store.mergeCards(groupId, deckId, [
        {
          ...existing,
          deletedTimeISO: msg.deletedTimeISO,
          updatedAtISO: msg.deletedTimeISO,
        },
      ]);
    }
    return;
  }

  if (entity.type === 'record') {
    if (entity.name === 'deckProperties') {
      const existing = store.groups[groupId]?.deckProps[msg.key];
      if (existing) {
        store.mergeDeckProps(groupId, [
          {
            ...existing,
            deletedTimeISO: msg.deletedTimeISO,
            updatedAtISO: msg.deletedTimeISO,
          },
        ]);
      }
      return;
    }
    if (entity.name === 'sgDeckMindmaps') {
      const existing = store.groups[groupId]?.deckMindmaps?.[msg.key];
      if (existing) {
        store.upsertDeckMindmap(groupId, {
          ...existing,
          deletedTimeISO: msg.deletedTimeISO,
          updatedAtISO: msg.deletedTimeISO,
        });
      }
      return;
    }
    if (entity.name === 'sgActivity') {
      // Activity is append-only — synthesize a deleted entry so the
      // upsertActivity setter records the tombstone.
      store.upsertActivity(groupId, {
        id: msg.key,
        type: 'deck_removed',
        userId: 'system',
        createdTimeISO: '',
        deletedTimeISO: msg.deletedTimeISO || new Date().toISOString(),
      });
    }
  }
}

function applySgBulkUpdate(
  groupId: string,
  entity: ClientSyncEntity,
  msg: SgBulkUpdateBroadcast,
): void {
  const entries = msg.entries ?? [];
  if (entries.length === 0) return;

  // SG nested-record (cards): group the entries per deck and land the whole
  // frame in ONE store tick — a sync-init frame can span several small decks,
  // and each tick runs every SG store subscriber.
  if (entity.type === 'record' && entity.nested) {
    const store = useStudyGroupStore.getState();
    const perDeck: Record<string, SyncCardProps[]> = {};
    for (const e of entries) {
      const sep = e.key.indexOf('\x01');
      if (sep === -1) continue;
      const deckId = e.key.slice(0, sep);
      (perDeck[deckId] ??= []).push(e.entry as unknown as SyncCardProps);
    }
    if (Object.keys(perDeck).length === 0) return;
    store.mergeCardsBatch(groupId, perDeck);
    for (const [deckId, cards] of Object.entries(perDeck)) {
      recordReceivedSgCardClock(groupId, deckId, cards);
    }
    return;
  }

  // Other SG record entities (deckProperties, sgActivity): synthesize
  // single-entry messages and re-use the single dispatcher so the
  // per-entity merge logic stays single-sourced.
  for (const e of entries) {
    applySgUpdate(groupId, entity, {
      key: e.key,
      entry: e.entry,
      updatedAtISO: e.updatedAtISO,
    } as Record<string, unknown>);
  }
}

function applySgBulkDelete(
  groupId: string,
  entity: ClientSyncEntity,
  msg: SgBulkDeleteBroadcast,
): void {
  const keys = msg.keys ?? [];
  if (keys.length === 0) return;
  const fallbackTs = new Date().toISOString();
  for (const k of keys) {
    applySgDelete(groupId, entity, {
      type: '',
      key: k.key,
      deletedTimeISO: k.deletedTimeISO ?? fallbackTs,
    });
  }
}

// ---------------------------------------------------------------------------
// Sync-diff application
// ---------------------------------------------------------------------------

function applySgSyncDiff(groupId: string, msg: SGSyncDiffMsg): void {
  const store = useStudyGroupStore.getState();
  const generics = msg.generics ?? {};
  const wireMsg = msg as unknown as Record<string, unknown>;

  for (const entity of clientGroupEntities) {
    if (entity.type === 'value' || entity.type === 'collection') {
      const blob = (generics as Record<string, unknown>)[entity.stateKey];
      if (!blob) continue;
      // Meta has an extra LWW guard: a stale server snapshot must not
      // clobber an unpushed local edit. Collections handle their own
      // per-row LWW so plain dispatch is safe.
      if (entity.name === 'sgMeta') {
        const env = blob as { sgMeta: StudyGroupMeta; updatedAtISO: string };
        const local = store.groups[groupId]?.meta;
        if (!local || (env.sgMeta.updatedAtISO ?? '') >= (local.updatedAtISO ?? '')) {
          store.setMeta(groupId, env.sgMeta);
        }
        store.setServerTs(groupId, sgTsKey(entity), env.updatedAtISO);
        continue;
      }
      applySgUpdate(groupId, entity, blob as Record<string, unknown>);
      continue;
    }

    if (entity.type === 'record' && !entity.nested && entity.name !== 'cards') {
      const map = wireMsg[entity.stateKey] as Record<string, LWWItem> | undefined;
      if (!map) continue;
      const rows = Object.values(map);
      if (rows.length === 0) continue;
      if (entity.name === 'deckProperties') {
        const kept = mergeSgDeckPropsKeepingLocal(
          groupId,
          rows as unknown as SGDeckProps[],
        );
        const maxTs = rows.reduce(
          (acc, r) => ((r.updatedAtISO ?? '') > acc ? (r.updatedAtISO ?? acc) : acc),
          '',
        );
        // A row the merge kept local must stay past the cursor so the flush
        // that follows this diff pushes it — see `mergeSgDeckPropsKeepingLocal`.
        if (maxTs && !kept) store.setServerTs(groupId, sgTsKey(entity), maxTs);
      } else if (entity.name === 'sgActivity') {
        for (const entry of rows) {
          store.upsertActivity(groupId, entry as unknown as SGActivityEntry);
        }
      } else if (entity.name === 'sgDeckMindmaps') {
        for (const [key, entry] of Object.entries(map)) {
          store.upsertDeckMindmap(groupId, {
            ...(entry as object),
            key,
          } as SGDeckMindmapEntry);
        }
      }
    }
  }

  // Per-deck section — the manifest of every deck the client was behind on,
  // each with its meta. A streaming server ships the cards as frames after
  // this header (`cards` is empty here); a one-message server inlines them,
  // in which case merge them and record the shared-card clock so the received
  // cards aren't echoed back as a flush storm on reconnect.
  for (const [deckId, deck] of Object.entries(msg.decks ?? {})) {
    store.mergeDeckProps(groupId, [deck.properties], serverRowViewer());
    const cards = deck.cards ?? [];
    if (cards.length === 0) continue;
    store.mergeCards(groupId, deckId, cards);
    recordReceivedSgCardClock(groupId, deckId, cards);
  }
}

/** The decks of a one-message diff whose card set is complete once applied:
 *  every shipped deck that is not a tombstone. */
function liveDeckIds(diff: SGSyncDiffMsg): string[] {
  const ids: string[] = [];
  for (const [deckId, deck] of Object.entries(diff.decks ?? {})) {
    if (!deck.properties?.deletedTimeISO) ids.push(deckId);
  }
  return ids;
}

// ---------------------------------------------------------------------------
// Outbound dirty flush
// ---------------------------------------------------------------------------

function flushSgLocalDirty(groupId: string, send: (raw: string) => void): void {
  // Deck removals the group never answered (sent into a dead socket, or
  // before an app restart) — re-sent until the group accepts or refuses.
  resendPendingDeckRemovals(groupId, send);
  const slice = useStudyGroupStore.getState().groups[groupId];
  if (!slice) return;
  const now = new Date().toISOString();
  const serverTs = slice.serverTimestamps ?? {};

  // Meta
  if (
    slice.meta?.updatedAtISO &&
    (slice.meta.updatedAtISO ?? '') > (serverTs.__sgMeta ?? '')
  ) {
    send(
      JSON.stringify({
        type: 'sg-meta-push',
        sgMeta: slice.meta,
        updatedAtISO: slice.meta.updatedAtISO ?? now,
      }),
    );
  }

  // Members
  const dirtyMembers = Object.values(slice.members).filter(
    (m) => (m.updatedAtISO ?? '') > (serverTs.__sgMembers ?? ''),
  );
  if (dirtyMembers.length > 0) {
    send(
      JSON.stringify({
        type: 'sg-members-push',
        sgMembers: dirtyMembers,
        updatedAtISO: now,
      }),
    );
  }

  // Folders
  const dirtyFolders = Object.values(slice.folders).filter(
    (f) => (f.updatedAtISO ?? '') > (serverTs.__sgFolders ?? ''),
  );
  if (dirtyFolders.length > 0) {
    send(
      JSON.stringify({
        type: 'sg-folders-push',
        sgFolders: dirtyFolders,
        updatedAtISO: now,
      }),
    );
  }

  // Deck properties — per-row framework record wire.
  const dirtyDeckProps = Object.values(slice.deckProps).filter(
    (d) => (d.updatedAtISO ?? '') > (serverTs.__sgdeckmeta ?? ''),
  );
  for (const row of dirtyDeckProps) {
    const version = row.updatedAtISO ?? now;
    const versionKey = flushVersionKey(
      groupId,
      'sg-deckProperties-push',
      row.id,
      version,
    );
    // The server already refused this exact version (e.g. a non-owner
    // editing an owner-only deck's properties). Don't re-push it on every
    // reconnect — that's the sg-sync-error loop. A genuinely newer edit
    // produces a new version key and is retried.
    if (isFlushVersionRejected(versionKey)) continue;
    const mutationId = generateMutationId();
    recordFlushMutation(mutationId, versionKey);
    send(
      JSON.stringify({
        type: 'sg-deckProperties-push',
        key: row.id,
        entry: row,
        updatedAtISO: version,
        mutationId,
      }),
    );
  }

  // Cards — push when the card's SHARED timestamp is ahead of the deck's
  // shared-card clock (`__sgcards:<deckId>`, what the server has confirmed).
  // Gating on the shared scope means a personal-only edit (bumps
  // updatedAtISO but not sharedUpdatedAtISO) is NOT treated as dirty, and a
  // card received from a peer (which advanced the clock) is not echoed back.
  //
  // The wire is `sg-cards-bulk-push` (not the per-row `sg-cards-push`) so a
  // reconnect with N dirty cards emits ceil(N / MAX_CARDS_PER_BULK) frames,
  // not N. At >250 dirty cards / 10s the per-row loop would trip the WS
  // rate-limiter (4429 close). The server's `handleGroupBulkPush` for the
  // cards entity applies the same per-entry LWW gate the single-row path
  // does, and the dispatch emits one `op-ack` per envelope — so a chunk's
  // `mutationId` settles the whole batch via `advanceSgCardClockFromAck`.
  for (const [deckId, cardMap] of Object.entries(slice.cards)) {
    const deckCardTs = serverTs[`__sgcards:${deckId}`] ?? '';
    const dirty: Array<{ key: string; entry: SyncCardProps; updatedAtISO: string }> = [];
    for (const c of Object.values(cardMap)) {
      const sharedTs = c.sharedUpdatedAtISO ?? c.updatedAtISO ?? '';
      if (!sharedTs || sharedTs <= deckCardTs) continue;
      dirty.push({ key: `${deckId}\x01${c.id}`, entry: c, updatedAtISO: sharedTs });
    }
    if (dirty.length === 0) continue;

    // Chunk so a single envelope can't blow the SQLite cell ceiling
    // (cards embed shared-front/back text) and so one frame stays well
    // under the rate-limit window. Each chunk gets its own mutationId
    // and version key so a reject on chunk 2 doesn't suppress chunk 1.
    for (let i = 0; i < dirty.length; i += MAX_CARDS_PER_BULK) {
      const chunk = dirty.slice(i, i + MAX_CARDS_PER_BULK);
      let maxSharedTs = '';
      for (const e of chunk) {
        if (e.updatedAtISO > maxSharedTs) maxSharedTs = e.updatedAtISO;
      }
      const versionKey = flushVersionKey(
        groupId,
        'sg-cards-bulk-push',
        deckId,
        maxSharedTs,
      );
      if (isFlushVersionRejected(versionKey)) continue;
      const mutationId = generateMutationId();
      recordFlushMutation(mutationId, versionKey);
      send(
        JSON.stringify({
          type: 'sg-cards-bulk-push',
          entries: chunk,
          updatedAtISO: maxSharedTs,
          mutationId,
        }),
      );
    }
  }
}

/**
 * Maximum cards per `sg-cards-bulk-push` envelope. Picked so a single frame
 * stays comfortably under the WS rate-limiter window (250 msgs / 10s) and
 * the SG DO's SQLite write ceiling, even with shared-front/back text
 * embedded. Exported so tests can assert the chunking shape against the
 * runtime value instead of a hard-coded literal.
 */
export const MAX_CARDS_PER_BULK = 100;

// ---------------------------------------------------------------------------
// Deck shared-card clock — records what the server has confirmed
// ---------------------------------------------------------------------------

/**
 * Advance the deck's shared-card clock (`serverTimestamps['__sgcards:<deckId>']`)
 * past the newest SHARED timestamp among cards we just received. This is
 * the baseline `flushSgLocalDirty` compares each card's `sharedUpdatedAtISO`
 * against, so a card another member edited (which advanced the clock) is
 * not echoed back as a spurious `sg-cards-push` on reconnect.
 *
 * Replaces the former `bumpDeckPropsFromReceivedCards` band-aid: the clock
 * lives in transport bookkeeping (serverTimestamps), not in the deck's
 * domain `updatedAtISO`/`lastPushedAtISO`, so received card traffic no
 * longer corrupts deck-property LWW.
 */
function recordReceivedSgCardClock(
  groupId: string,
  deckId: string,
  incomingCards: SyncCardProps[],
): void {
  if (incomingCards.length === 0) return;
  let maxShared = '';
  for (const c of incomingCards) {
    const shared = c.sharedUpdatedAtISO ?? c.updatedAtISO ?? '';
    if (shared > maxShared) maxShared = shared;
  }
  if (!maxShared) return;
  const store = useStudyGroupStore.getState();
  const key = `__sgcards:${deckId}`;
  const current = store.groups[groupId]?.serverTimestamps?.[key] ?? '';
  if (maxShared > current) store.setServerTs(groupId, key, maxShared);
}

/**
 * A successful `sg-cards-push` op-ack is the server confirming OUR OWN
 * shared card. Advance the deck's shared-card clock past it so
 * `flushSgLocalDirty` stops treating the card as dirty on the next
 * sync-diff.
 *
 * The receive-side `recordReceivedSgCardClock` only fires for cards we
 * are *sent* — it never runs for a deck the local user owns (the server
 * withholds those, since sync-init reports we already have them). Without
 * advancing the clock on ack, every sync-diff re-pushes the whole owned
 * deck and each push is acked, forever (the op-ack churn storm).
 */
function advanceSgCardClockFromAck(mutationId: string): void {
  const versionKey = peekFlushMutationVersion(mutationId);
  if (!versionKey) return;
  const parts = versionKey.split('\x01');
  // Per-row push: `groupId \x01 sg-cards-push \x01 deckId \x01 cardId \x01 sharedTs`
  //   (5 parts — the row key embeds its own \x01 between deckId and cardId).
  // Bulk push:    `groupId \x01 sg-cards-bulk-push \x01 deckId \x01 maxSharedTs`
  //   (4 parts — one ack settles the whole envelope; the clock advances to
  //   the chunk's max shared timestamp).
  let gId: string | undefined;
  let deckId: string | undefined;
  let sharedTs: string | undefined;
  if (parts.length === 5 && parts[1] === 'sg-cards-push') {
    [gId, , deckId, , sharedTs] = parts;
  } else if (parts.length === 4 && parts[1] === 'sg-cards-bulk-push') {
    [gId, , deckId, sharedTs] = parts;
  } else {
    return;
  }
  if (!gId || !deckId || !sharedTs) return;
  const store = useStudyGroupStore.getState();
  const key = `__sgcards:${deckId}`;
  const current = store.groups[gId]?.serverTimestamps?.[key] ?? '';
  if (sharedTs > current) store.setServerTs(gId, key, sharedTs);
}

/**
 * The deck-meta twin of `advanceSgCardClockFromAck`: a successful
 * `sg-deckProperties-push` op-ack is the server confirming a row this
 * device pushed, so the group's deck-meta cursor moves past its version.
 * Without it a row the diff-time merge kept local (see
 * `mergeSgDeckPropsKeepingLocal`) holds the cursor back for good, and every
 * reconnect re-pushes it — a no-op on the server, but a push each time.
 */
function advanceSgDeckMetaCursorFromAck(mutationId: string): void {
  const versionKey = peekFlushMutationVersion(mutationId);
  if (!versionKey) return;
  // `groupId \x01 sg-deckProperties-push \x01 deckId \x01 version` (4 parts).
  const parts = versionKey.split('\x01');
  if (parts.length !== 4 || parts[1] !== 'sg-deckProperties-push') return;
  const [gId, , , version] = parts;
  if (!gId || !version) return;
  const store = useStudyGroupStore.getState();
  const current = store.groups[gId]?.serverTimestamps?.__sgdeckmeta ?? '';
  if (version > current) store.setServerTs(gId, '__sgdeckmeta', version);
}
